import { useState } from 'react'
import { LogIn, LogOut, MapPin, Navigation, ScanFace } from 'lucide-react'
import { Callout, ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import {
  uploadPunchPhoto, useMyFace, useMyGeofence, useMyPunches, usePunch, useRegisterMyFace,
} from '../api/geofence'
import { CameraCapture } from '../components/geofence/CameraCapture'
import type { Shot } from '../components/geofence/CameraCapture'
import { currentPosition, formatMetres } from '../components/geofence/geo'
import type { Position } from '../components/geofence/geo'
import { FACE_RESULTS, GEOFENCE_MODES, GEOFENCE_RESULTS } from '../types/geofence'

type Direction = 'IN' | 'OUT'

interface Pending {
  direction: Direction
  position: Position | null
  selfieFileId: string | null
}

const REGISTRATION_PROMPTS = [
  'Look straight at the camera',
  'Turn your head slightly to the left',
  'Turn your head slightly to the right',
]

/**
 * Punching in and out from the browser, with the location and face rules explained up front.
 *
 * <p>The position is read when a button is pressed, never before. When the policy needs a
 * photo, the camera opens; a face that does not match keeps the camera open with the reason, so
 * a retake is one tap rather than starting over. When the policy accepts a reason for being
 * outside the fence, a refusal asks for one instead of being a dead end.
 */
export function MyPunchPage() {
  const breadcrumb = useBreadcrumb()
  const mine = useMyGeofence()
  const face = useMyFace()
  const history = useMyPunches()
  const punch = usePunch()
  const registerFace = useRegisterMyFace()

  const [busy, setBusy] = useState<Direction | null>(null)
  const [message, setMessage] = useState<{ tone: 'safe' | 'warning'; text: string } | null>(null)
  const [camera, setCamera] = useState<{ direction: Direction; position: Position | null } | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [needsReason, setNeedsReason] = useState<(Pending & { text: string }) | null>(null)
  const [reason, setReason] = useState('')
  const [consenting, setConsenting] = useState(false)
  const [consented, setConsented] = useState(false)
  const [registering, setRegistering] = useState(false)
  const [registerError, setRegisterError] = useState<string | null>(null)

  const policy = mine.data
  const faceOn = Boolean(policy?.faceMatch && policy.faceMatch !== 'OFF')
  const photoNeeded = Boolean(policy?.photoRequired || faceOn)
  const mustRegister = faceOn && face.data && !face.data.registered

  /** Sends the punch. Returns false when the camera should stay open for a retake. */
  async function send(pending: Pending, outsideReason?: string): Promise<boolean> {
    try {
      const result = await punch.mutateAsync({
        direction: pending.direction,
        latitude: pending.position?.latitude ?? null,
        longitude: pending.position?.longitude ?? null,
        accuracyMetres: pending.position?.accuracy ?? null,
        outsideReason: outsideReason ?? null,
        selfieFileId: pending.selfieFileId,
      })
      setNeedsReason(null)
      setReason('')
      const flagged = result.geofenceResult === 'FLAGGED'
        || (result.faceResult != null && !['MATCHED', 'NOT_REQUIRED'].includes(result.faceResult))
      setMessage({
        tone: flagged ? 'warning' : 'safe',
        text: `Punched ${pending.direction === 'IN' ? 'in' : 'out'} at ${new Date(result.punchTime).toLocaleTimeString()}. `
          + [result.geofenceNote, result.faceNote].filter(Boolean).join(' '),
      })
      return true
    } catch (caught) {
      const problem = toApiProblem(caught)
      if (problem?.code === 'punch.face_mismatch' || problem?.code === 'punch.photo_required') {
        setCameraError(problem.detail)
        return false
      }
      if (problem?.reasonAccepted === true && pending.position && !outsideReason) {
        setNeedsReason({ ...pending, text: problem.detail })
        return true
      }
      if (problem?.code === 'punch.face_not_registered') void face.refetch()
      setMessage({ tone: 'warning', text: problem?.detail ?? 'The punch could not be recorded.' })
      return true
    }
  }

  async function press(direction: Direction) {
    setMessage(null)
    if (mustRegister) {
      setMessage({ tone: 'warning', text: 'Register your face first — your punches are checked against it.' })
      return
    }
    setBusy(direction)
    let position: Position | null = null
    try {
      position = await currentPosition()
    } catch (caught) {
      if (policy?.mandatory) {
        setMessage({ tone: 'warning', text: (caught as Error).message })
        setBusy(null)
        return
      }
    }
    if (photoNeeded) {
      setCameraError(null)
      setCamera({ direction, position })
      return
    }
    await send({ direction, position, selfieFileId: null })
    setBusy(null)
  }

  async function withPhoto(shots: Shot[]) {
    if (!camera || !shots[0]) return
    setCameraError(null)
    try {
      const selfieFileId = await uploadPunchPhoto(shots[0].blob)
      const finished = await send({ ...camera, selfieFileId })
      if (finished) {
        setCamera(null)
        setBusy(null)
      }
    } catch (caught) {
      setCameraError(toApiProblem(caught)?.detail ?? 'The photo could not be uploaded.')
    }
  }

  function register(shots: Shot[]) {
    setRegisterError(null)
    registerFace.mutate(shots.map((shot) => shot.blob), {
      onSuccess: () => {
        setRegistering(false)
        setMessage({ tone: 'safe', text: 'Your face is registered. You can punch now.' })
      },
      onError: (caught) => setRegisterError(toApiProblem(caught)?.detail ?? 'Registration failed.'),
    })
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Punch in / out" breadcrumb={breadcrumb}
        subtitle="Record your attendance from this device." />

      {camera && <CameraCapture
        open
        title={camera.direction === 'IN' ? 'Punch in — take a selfie' : 'Punch out — take a selfie'}
        description={faceOn ? 'Your face is checked against your registered face.' : 'The photo is kept with your punch.'}
        prompts={['Face the camera in good light']}
        confirmLabel={camera.direction === 'IN' ? 'Punch in' : 'Punch out'}
        busy={punch.isPending}
        error={cameraError}
        onConfirm={(shots) => { void withPhoto(shots) }}
        onCancel={() => { setCamera(null); setBusy(null) }}
      />}

      {registering && <CameraCapture
        open
        title="Register your face"
        description="Three photos, a slightly different angle each time. Remove sunglasses and hats."
        count={3}
        prompts={REGISTRATION_PROMPTS}
        confirmLabel="Register"
        busy={registerFace.isPending}
        error={registerError}
        onConfirm={register}
        onCancel={() => setRegistering(false)}
      />}

      {consenting && face.data && (
        <FormDialog
          open
          onOpenChange={(open) => { if (!open) setConsenting(false) }}
          title="Before you register your face"
          submitLabel="Agree and continue"
          onSubmit={() => {
            if (!consented) return
            setConsenting(false)
            setRegisterError(null)
            setRegistering(true)
          }}
        >
          <p className="whitespace-pre-line text-sm text-[var(--color-body)]">{face.data.consentText}</p>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" className="mt-1 h-4 w-4 accent-[var(--color-primary)]"
              checked={consented} onChange={(event) => setConsented(event.target.checked)} />
            I have read this and agree.
          </label>
        </FormDialog>
      )}

      {needsReason && (
        <FormDialog
          open
          onOpenChange={(open) => { if (!open) setNeedsReason(null) }}
          title="You are outside your permitted site"
          description={needsReason.text}
          submitLabel={`Punch ${needsReason.direction === 'IN' ? 'in' : 'out'} with this reason`}
          loading={punch.isPending}
          onSubmit={() => { if (reason.trim()) void send(needsReason, reason.trim()) }}
        >
          <div className="space-y-1">
            <Label htmlFor="punch-reason" required>Why are you punching from here?</Label>
            <Textarea id="punch-reason" rows={3} maxLength={500} required value={reason}
              placeholder="e.g. Client visit at Dubai Mall; site inspection"
              onChange={(event) => setReason(event.target.value)} />
            <p className="text-xs text-[var(--color-muted)]">Your manager will see this with the punch.</p>
          </div>
        </FormDialog>
      )}

      {mustRegister && (
        <Callout tone="warning" title="Register your face to start punching">
          <p>Your attendance is checked against a registered photo of your face. It takes under a minute.</p>
          <Button type="button" className="mt-3" onClick={() => { setConsented(false); setConsenting(true) }}>
            <ScanFace className="size-4" aria-hidden="true" />
            Register my face
          </Button>
        </Callout>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
        <Card className="p-5">
          <div className="grid grid-cols-2 gap-3">
            <Button type="button" className="h-20 text-base" disabled={busy !== null}
              onClick={() => { void press('IN') }}>
              <LogIn className="size-5" aria-hidden="true" />
              {busy === 'IN' ? 'Working…' : 'Punch in'}
            </Button>
            <Button type="button" variant="outline" className="h-20 text-base" disabled={busy !== null}
              onClick={() => { void press('OUT') }}>
              <LogOut className="size-5" aria-hidden="true" />
              {busy === 'OUT' ? 'Working…' : 'Punch out'}
            </Button>
          </div>
          <p className="mt-3 flex items-center gap-1 text-xs text-[var(--color-muted)]">
            <Navigation className="size-3" aria-hidden="true" />
            Your location{photoNeeded ? ' and camera are' : ' is'} used only when you press a button.
          </p>
          {message && (
            <div className="mt-4">
              <Callout tone={message.tone}>{message.text}</Callout>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
            <MapPin className="size-4" aria-hidden="true" />
            Your punch rules
          </h3>
          {mine.isPending && <Skeleton className="h-20" />}
          {mine.isError && <ErrorState error={mine.error} onRetry={() => void mine.refetch()} />}
          {policy && !policy.policyId && (
            <p className="text-sm text-[var(--color-muted)]">No punch policy applies to you.</p>
          )}
          {policy?.policyId && policy.mode && (
            <div className="space-y-2 text-sm">
              <p className="text-[var(--color-heading)]">{GEOFENCE_MODES[policy.mode].label}</p>
              {policy.mode !== 'UNRESTRICTED' && (
                <ul className="space-y-1">
                  {policy.permittedSites.map((site) => (
                    <li key={site.id} className="flex items-center justify-between gap-2">
                      <span>{site.name}</span>
                      <span className="text-xs text-[var(--color-muted)]">
                        {site.fenced ? `within ${formatMetres(site.radiusMetres)}` : 'fence not set up'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {policy.allowOutsideWithReason && (
                <p className="text-xs text-[var(--color-muted)]">If you are elsewhere you can still punch by giving a reason.</p>
              )}
              {photoNeeded && (
                <p className="text-xs text-[var(--color-muted)]">
                  {faceOn ? 'A selfie is taken and checked against your registered face.' : 'A selfie is taken with each punch.'}
                </p>
              )}
              {faceOn && face.data?.registered && (
                <p className="flex items-center gap-1 text-xs text-[var(--color-muted)]">
                  <ScanFace className="size-3" aria-hidden="true" />
                  Face registered{face.data.registeredAt ? ` on ${new Date(face.data.registeredAt).toLocaleDateString()}` : ''}.
                  To change it, ask HR to reset it.
                </p>
              )}
              {policy.livenessRequired && (
                <p className="text-xs text-[var(--color-danger)]">
                  Your policy needs a live-face check, which only the mobile app can do. Punch from the app.
                </p>
              )}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="mb-3 text-sm font-semibold text-[var(--color-heading)]">Recent punches</h3>
        {history.isPending && <Skeleton className="h-24" />}
        {history.data?.length === 0 && <p className="text-sm text-[var(--color-muted)]">No punches from the app yet.</p>}
        <ul className="divide-y divide-[var(--color-border)]">
          {history.data?.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <span>
                <span className="font-medium text-[var(--color-heading)]">{row.direction === 'IN' ? 'In' : 'Out'}</span>
                {' · '}
                {new Date(row.punchTime).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
              </span>
              <span className="flex flex-wrap gap-1">
                <StatusBadge status={row.geofenceResult}
                  tone={GEOFENCE_RESULTS[row.geofenceResult]?.tone ?? 'neutral'}
                  label={GEOFENCE_RESULTS[row.geofenceResult]?.label ?? row.geofenceResult} />
                {row.faceResult && row.faceResult !== 'NOT_REQUIRED' && (
                  <StatusBadge status={row.faceResult}
                    tone={FACE_RESULTS[row.faceResult]?.tone ?? 'neutral'}
                    label={FACE_RESULTS[row.faceResult]?.label ?? row.faceResult} />
                )}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  )
}
